import type { ComponentProps, ComponentState, Slot, Button, SearchBox } from '@fluentui/react-components';

export type GroundingMenuSearchBoxSlots = {
  root: NonNullable<Slot<'div'>>;
  expandButton?: Slot<typeof Button>;
  searchBox?: Slot<typeof SearchBox>;
};

/**
 * GroundingMenuSearchBox Props
 */
export type GroundingMenuSearchBoxProps = ComponentProps<Partial<GroundingMenuSearchBoxSlots>> & {
  /**
   * Whether the search box is visible or the collapsed button is visible.
   * @default false
   */
  expanded?: boolean;
  /**
    Whether or not the initial state of the search box is expanded when `expanded` is uncontrolled.
    @default false
  */
  defaultExpanded?: boolean;
  /**
   * Whether the search box should be focused when expanded from previously being collapsed.
   * If true, this will only focus the search box when the search box is expanded after previously being collapsed.
   * A search box that is expanded by default with the `expanded` prop will not be automatically focused.
   * @default true
   */
  focusSearchBoxOnExpand?: boolean;
  /**
   * Callback that will be triggered when the search box is expanded or collapsed
   * If the `expanded` prop is controlled by setting it to `true` or `false`, the search box will not automatically change.
   * In that case, this callback will still fire and indicate when the search box would have changed if `expanded` was uncontrolled.
   * @param newState The new expanded state.
   */
  onExpandedChange?: (newState: GroundingMenuSearchBoxExpandedData) => void;
  /**
   * By default, the search box will not automatically collapse if focus moves specifically to the GroundingCommandBarTabList.
   * Set this to `true` to override this behaviour and always (when uncontrolled) collapse the search box when blurred.
   * @default false
   */
  collapseOnUseTabs?: boolean;
};

export type GroundingMenuSearchBoxExpandedData = {
  expanded: boolean;
};

/**
 * State used in rendering GroundingMenuSearchBox
 */
export type GroundingMenuSearchBoxState = ComponentState<GroundingMenuSearchBoxSlots> &
  Required<Pick<GroundingMenuSearchBoxProps, 'expanded'>>;
